import { useParams } from "react-router-dom";
import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { publicFetch, PublicApiError } from "@/api/public-fetch";

interface QuestionData {
  question_text: string;
  question_day: number;
  expires_at: string;
  already_responded: boolean;
}

interface SubmitResponse {
  success: boolean;
  message: string;
}

export function DailyQuestionPage() {
  const { token } = useParams<{ token: string }>();
  const [responseText, setResponseText] = useState("");
  const [submitted, setSubmitted] = useState(false);

  // Fetch question data
  const {
    data: questionData,
    isLoading,
    error,
  } = useQuery<QuestionData>({
    queryKey: ["daily-question", token],
    queryFn: () => publicFetch<QuestionData>(`/daily-question/${token}`),
    enabled: !!token,
    retry: false,
  });

  // Submit response mutation
  const submitMutation = useMutation<SubmitResponse, Error, { response_text: string; timezone: string }>({
    mutationFn: (data) =>
      publicFetch<SubmitResponse>(`/daily-question/${token}/response`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      setSubmitted(true);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (responseText.trim()) {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      submitMutation.mutate({ response_text: responseText, timezone });
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading your question...</p>
        </div>
      </div>
    );
  }

  // Error state - handle different error types
  if (error) {
    const is410 = error instanceof PublicApiError && error.status === 410;
    const is404 = error instanceof PublicApiError && error.status === 404;

    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-md p-8 text-center">
          <div className="text-6xl mb-4">{is410 ? "⏰" : is404 ? "🔍" : "❌"}</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            {is410 ? "Link Expired" : is404 ? "Question Not Found" : "Error"}
          </h1>
          <p className="text-gray-600">
            {is410
              ? "This question link has expired. Daily questions are only available for 12 hours after they're sent."
              : is404
              ? "We couldn't find this question. Please check the link and try again."
              : "Something went wrong. Please try again later."}
          </p>
        </div>
      </div>
    );
  }

  // Already responded state
  if (questionData?.already_responded) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-md p-8 text-center">
          <div className="text-6xl mb-4">✅</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Already Answered</h1>
          <p className="text-gray-600">
            You've already answered this question. Check your group conversation for a summary once your partner responds!
          </p>
        </div>
      </div>
    );
  }

  // Success state (after submission)
  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-md p-8 text-center">
          <div className="text-6xl mb-4">🎉</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Thank You!</h1>
          <p className="text-gray-600 mb-4">
            Your response has been saved. We'll share a thoughtful summary with both of you once your partner responds.
          </p>
          <p className="text-sm text-gray-500">You can close this page now.</p>
        </div>
      </div>
    );
  }

  // Main form state
  return (
    <div className="fixed inset-0 bg-gray-50 flex items-center justify-center px-4 py-8">
      <div className="max-w-2xl w-full max-h-full overflow-y-auto bg-white rounded-lg shadow-md p-8">
        {/* Header */}
        <div className="mb-6">
          <div className="text-sm text-gray-500 mb-2">Question {questionData?.question_day}</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-1">Daily Question</h1>
          <p className="text-sm text-gray-500">
            Expires {new Date(questionData?.expires_at || "").toLocaleString()}
          </p>
        </div>

        {/* Question */}
        <div className="bg-blue-50 border-l-4 border-blue-500 p-6 mb-6">
          <p className="text-lg text-gray-800 leading-relaxed">{questionData?.question_text}</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <div className="mb-6">
            <label htmlFor="response" className="block text-sm font-medium text-gray-700 mb-2">
              Your Response
            </label>
            <textarea
              id="response"
              rows={6}
              className="w-full px-4 py-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              placeholder="Share your thoughts..."
              value={responseText}
              onChange={(e) => setResponseText(e.target.value)}
              required
              disabled={submitMutation.isPending}
            />
            <p className="mt-2 text-sm text-gray-500">
              Your response will be shared with your partner in a thoughtful summary once you both answer.
            </p>
          </div>

          {/* Error message */}
          {submitMutation.isError && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-800">
                {submitMutation.error instanceof Error
                  ? submitMutation.error.message
                  : "Failed to submit response. Please try again."}
              </p>
            </div>
          )}

          {/* Submit button */}
          <button
            type="submit"
            disabled={!responseText.trim() || submitMutation.isPending}
            className="w-full bg-blue-600 text-white font-medium py-4 px-6 rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            {submitMutation.isPending ? "Submitting..." : "Submit Response"}
          </button>
        </form>
      </div>
    </div>
  );
}
